import { lazy, Suspense, useCallback, useState } from "react";

import FullSizeLoading from "components/FullSizeLoading";
import useGetUserInfo from "hooks/api/useGetUserInfo";
import useUpdateEmailSetting from "hooks/api/useUpdateEmailSetting";
import { useSyncEffect } from "hooks/effects";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import { useInfoModal } from "hooks/useModal";
import { isBrowser, isTablet } from "utils/deviceDetect";

const EmailSettingPresenter = lazy(() =>
  isBrowser
    ? import("pages/EmailSetting/pc/EmailSettingPresenter")
    : isTablet
      ? import("pages/EmailSetting/pc/EmailSettingPresenter")
      : import("pages/EmailSetting/pc/EmailSettingPresenter")
);

const EmailSettingContainer = () => {
  const { userInfo, mutate } = useGetUserInfo();
  const { updateEmailSetting } = useUpdateEmailSetting();
  const { InfoModal, showInfoModal } = useInfoModal();
  const { showErrorModal } = useErrorModalStatusContext();

  const [emailSetting, setEmailSetting] = useState<"ALL" | "NONE">("ALL");
  const [infoModalState, setInfoModalState] = useState<{ title: string; onClick: () => void }>({
    title: "",
    onClick: () => null,
  });

  const canSubmit = emailSetting !== userInfo?.emailNotification;

  const handleChangeEmailSetting = (v: "ALL" | "NONE") => {
    setEmailSetting(v);
  };

  const onSubmit = async () => {
    const status = await updateEmailSetting(emailSetting).catch((e) => {
      showErrorModal({
        httpMethod: "put",
      });
      throw e;
    });
    switch (status) {
      case 200:
        setInfoModalState({
          title: "受信設定を変更しました",
          onClick: () => mutate(),
        });
        showInfoModal();
        break;
      default:
        break;
    }
  };

  const setUp = useCallback(() => {
    if (userInfo) setEmailSetting(userInfo.emailNotification ?? "ALL");
  }, [userInfo, setEmailSetting]);

  useSyncEffect(() => {
    setUp();
  }, [setUp]);

  return (
    <Suspense fallback={<FullSizeLoading />}>
      <EmailSettingPresenter
        emailSetting={emailSetting}
        handleChangeEmailSetting={handleChangeEmailSetting}
        buttonDisabled={!canSubmit}
        onSubmit={onSubmit}
      />
      <InfoModal title={infoModalState.title} onClick={infoModalState.onClick} />
    </Suspense>
  );
};

export default EmailSettingContainer;
